<template>
  <div
    v-for="(comp, i) in visibleCanvasContent"
    :key="comp.id"
    v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, comp)"
    class="component-wrapper relative"
    :data-component-id="comp.id"
    :style="getTopLevelComponentStyle(i, comp)"
  >
    <CommentableComponent v-if="comp.type !== 'Page'" :component-id="comp.id">
      <LinkableComponent :id="comp.id">
        <Component
          :is="componentMap[comp.type]"
          v-bind="getComponentBindings(comp)"
          @style="
            (style: Record<string, any>, componentId?: ComponentNode['id']) =>
              onComponentUpdateStyle(style, componentId ?? comp.id)
          "
        />
      </LinkableComponent>
    </CommentableComponent>
    <Page
      v-else
      v-bind="getPageBindings(comp)"
      @style="
        (style: Record<string, any>, componentId?: ComponentNode['id']) =>
          onComponentUpdateStyle(style, componentId ?? comp.id)
      "
    >
      <div
        v-for="(c, i) in comp.children"
        :key="c.id"
        v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, c)"
        class="component-wrapper relative"
        :data-component-id="c.id"
        :style="getPageChildComponentStyle(i, comp, c)"
      >
        <CommentableComponent :component-id="c.id">
          <LinkableComponent :id="c.id">
            <Component
              :is="componentMap[c.type]"
              v-bind="getComponentBindings(c)"
              @style="
                (style: CSSProperties, componentId?: ComponentNode['id']) =>
                  onComponentUpdateStyle(style, componentId ?? c.id)
              "
            />
          </LinkableComponent>
        </CommentableComponent>
      </div>
    </Page>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef, type CSSProperties } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import { useSwipe } from '@vueuse/core'
import { cloneDeep, omit } from 'lodash-es'

import Block from '@canvas-builder/components/ui/Block/Block.vue'
import Carousel from '@canvas-builder/components/ui/Carousel/Carousel.vue'
import CollectionPlayer from '@canvas-builder/components/ui/CollectionPlayer/CollectionPlayer.vue'
import ContentGrid from '@canvas-builder/components/ui/ContentGrid/ContentGrid.vue'
import CustomDataTable from '@canvas-builder/components/ui/CustomDataTable/CustomDataTable.vue'
import DataCharts from '@canvas-builder/components/ui/DataCharts/DataCharts.vue'
import DynamicDataTable from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.vue'
import Embeddable from '@canvas-builder/components/ui/Embeddable/Embeddable.vue'
import Grid from '@canvas-builder/components/ui/Grid/Grid.vue'
import Html from '@canvas-builder/components/ui/Html/Html.vue'
import IFrame from '@canvas-builder/components/ui/IFrame/IFrame.vue'
import Link from '@canvas-builder/components/ui/Link/Link.vue'
import Multimedia from '@canvas-builder/components/ui/Multimedia/Multimedia.vue'
import Page from '@canvas-builder/components/ui/Page/Page.vue'
import PageBreak from '@canvas-builder/components/ui/PageBreak/PageBreak.vue'
import Scribble from '@canvas-builder/components/ui/Scribble/Scribble.vue'
import SectionList from '@canvas-builder/components/ui/SectionList/SectionList.vue'
import ContentList from '@canvas-builder/components/ui/ContentList/ContentList.vue'
import Text from '@canvas-builder/components/ui/Text/Text.vue'
import Timeline from '@canvas-builder/components/ui/Timeline/Timeline.vue'
import LinkableComponent from '@canvas-builder/components/container/LinkableComponent/LinkableComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import useEmbeddable from '@canvas-builder/composables/useEmbeddable'
import {
  ComponentTypes,
  type ComponentNode,
  type ComponentTrackingParams,
  type PageProps,
} from '@canvas-builder/types/canvas'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import CommentableComponent from '@lib/canvas-comments/components/CommentableComponent.vue'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility.ts'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

const componentMap = {
  Block,
  Carousel,
  CollectionPlayer,
  ContentGrid,
  CustomDataTable,
  DataCharts,
  DynamicDataTable,
  Embeddable,
  Grid,
  Html,
  IFrame,
  Link,
  Multimedia,
  PageBreak,
  Scribble,
  SectionList,
  ContentList,
  Text,
  Timeline,
} as Record<ComponentTypes, any>
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const { globalComponentSpacing } = useCanvasTheme()
const {
  isEditMode,
  canvasContent,
  onComponentEnter,
  onComponentExit,
  contentRef,
  activateNavigationItem,
  deactivateNavigationItem,
  patchNodeById,
  isPrintDisplayMode,
} = useCanvas()
const createdInEditMode = isEditMode.value
const { goToNextPage, goToPreviousPage, keepActivePageValid } = useCanvasPages()
const { hasZeroHeight } = useEmbeddable()
keepActivePageValid()

const visibleCanvasContent: ComputedRef<ComponentNode[]> = computed(() => {
  const { getVisibleCanvasContent, getVisibleKey } = useCanvasVisibility()
  const visibleKey = getVisibleKey()
  return getVisibleCanvasContent(cloneDeep(canvasContent.value), visibleKey)
})

const isPresentationWithNoCollapsibles = computed(
  () => (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles' && !isEditMode.value,
)

useSwipe(
  computed(() => contentRef.value?.$el),
  {
    onSwipeEnd: (e, direction) => {
      const isTouchInsideSwiper = !!(e.target as HTMLElement)?.closest('swiper-container')

      if (isEditMode.value || !isPresentationWithNoCollapsibles.value || isTouchInsideSwiper) return
      if (direction === 'left') {
        goToNextPage()
      } else if (direction === 'right') {
        goToPreviousPage()
      }
    },
  },
)

const onElementVisibility = (isVisible: boolean, c: ComponentNode) => {
  const params: ComponentTrackingParams = { trackable: true }
  if (isVisible) {
    onComponentEnter.value?.(c, params)

    if (c.type === ComponentTypes.SectionList) return

    activateNavigationItem(c.id, undefined, !createdInEditMode)
  } else {
    onComponentExit.value?.(c, params)

    if (c.type === ComponentTypes.SectionList) return

    deactivateNavigationItem(c.id, undefined, !createdInEditMode)
  }
}

function getPageBindings(component: ComponentNode) {
  return getComponentBindings(component) as Omit<ComponentNode<PageProps>, 'type'>
}

function getComponentBindings(component: ComponentNode) {
  return component.type === ComponentTypes.Grid || component.type === ComponentTypes.Scribble
    ? {
        ...omit(component, 'type'),
        style: {
          ...component?.style,
          marginBottom: undefined,
        } satisfies CSSProperties,
      }
    : {
        ...omit(component, 'children', 'type'),
        style: {
          ...component?.style,
          marginBottom: undefined,
        } satisfies CSSProperties,
      }
}

function onComponentUpdateStyle(style: CSSProperties | undefined, componentId: ComponentNode['id']) {
  patchNodeById(componentId, { style })
}

function getComponentStyleWithSpacing(
  shouldRemoveMargin: boolean,
  shouldRemoveMarginLegacy: boolean,
  component: ComponentNode,
  nextComponent?: ComponentNode,
): CSSProperties {
  const hasIntuitiveSpacing = launchDarkly.value.enable_more_intuitive_component_spacing

  if (hasIntuitiveSpacing) {
    const nextEmbeddableHasZeroHeight =
      nextComponent?.type === ComponentTypes.Embeddable && nextComponent.id && hasZeroHeight(nextComponent.id)

    return shouldRemoveMargin || nextEmbeddableHasZeroHeight
      ? {}
      : {
          marginBottom: component?.style?.marginBottom || `${globalComponentSpacing.value}px`,
        }
  } else {
    // Legacy behavior
    return shouldRemoveMarginLegacy
      ? {}
      : {
          marginBottom: component?.style?.marginBottom || `${globalComponentSpacing.value}px`,
        }
  }
}

function getTopLevelComponentStyle(componentIndex: number, component: ComponentNode): CSSProperties {
  const isLastComponent = componentIndex === visibleCanvasContent.value.length - 1
  const nextComponent =
    componentIndex < visibleCanvasContent.value.length - 1 ? visibleCanvasContent.value[componentIndex + 1] : undefined

  return getComponentStyleWithSpacing(
    isLastComponent,
    isPrintDisplayMode.value && isLastComponent,
    component,
    nextComponent,
  )
}

function getPageChildComponentStyle(
  childIndex: number,
  parentComponent: ComponentNode,
  childComponent: ComponentNode,
): CSSProperties {
  const childrenLength = parentComponent.children?.length ?? 0
  const isLastChild = childIndex === childrenLength - 1
  const isOnlyChild = childrenLength === 1
  const nextChild = childIndex < childrenLength - 1 ? parentComponent.children?.[childIndex + 1] : undefined

  return getComponentStyleWithSpacing(
    isLastChild || isOnlyChild,
    isPrintDisplayMode.value && isLastChild,
    childComponent,
    nextChild,
  )
}
</script>
